import React from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, Autocomplete, Typography, Stack, Switch, Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import TagSelect from './TagSelect';
import { computeDefaultEndDate, calculateDurationWeeks, formatWeeksDisplay } from '../../../../utils/experimentDuration';
import { formatLocalDate } from '../../../../utils/dateUtils';

const BulkMachineDiscount = React.forwardRef(({
  machines,
  onSubmit,
  defaultDates,
  availableTags,
  onValidityChange,
}, ref) => {
  const { t } = useTranslation();
  const [selectedMachine, setSelectedMachine] = React.useState(null);
  const [discount, setDiscount] = React.useState('');
  const [markup, setMarkup] = React.useState('');
  const [priceDirection, setPriceDirection] = React.useState('down');
  const isPriceIncrease = priceDirection === 'up';
  const [targetPrice, setTargetPrice] = React.useState('');
  const [pricingMode, setPricingMode] = React.useState('discount');
  const showCommaWarning = React.useMemo(() => pricingMode === 'price' && typeof targetPrice === 'string' && targetPrice.includes(','), [pricingMode, targetPrice]);
  const [dateFrom, setDateFrom] = React.useState(defaultDates?.from || null);
  const [dateTo, setDateTo] = React.useState(defaultDates?.to || null);
  const [tags, setTags] = React.useState([]);

  React.useEffect(() => {
    const nextFrom = defaultDates?.from || null;
    const nextTo = defaultDates?.to ?? (nextFrom ? computeDefaultEndDate(nextFrom) : null);
    setDateFrom(nextFrom);
    setDateTo(nextTo);
  }, [defaultDates?.from, defaultDates?.to]);

  const isValid = React.useMemo(() => {
    if (!selectedMachine || !dateFrom || !dateTo) {
      return false;
    }

    if (pricingMode === 'price') {
      const parsedPrice = parseFloat(targetPrice);
      return Number.isFinite(parsedPrice) && (isPriceIncrease ? parsedPrice > 0 : parsedPrice >= 0);
    }

    const parsed = Number(isPriceIncrease ? markup : discount);
    return Number.isFinite(parsed) && parsed >= 1 && parsed <= 100;
  }, [selectedMachine, discount, markup, targetPrice, dateFrom, dateTo, pricingMode, isPriceIncrease]);

  React.useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  const handleSubmit = React.useCallback(() => {
    if (!isValid) return false;
    const payload = {
      machineId: selectedMachine?.id,
      dateFrom: dateFrom ? formatLocalDate(dateFrom) : undefined,
      dateTo: dateTo ? formatLocalDate(dateTo) : undefined,
      tags,
      pricingMode,
    };

    // The direction rides inside the payload; the endpoint is still chosen by the sub-tab.
    if (isPriceIncrease) {
      payload.experimentType = 'alza_precio_sugerido';
    }

    if (pricingMode === 'price') {
      payload.targetPrice = parseFloat(targetPrice);
    } else if (isPriceIncrease) {
      payload.markup = Number(markup);
    } else {
      payload.discount = Number(discount);
    }

    return onSubmit(payload);
  }, [isValid, onSubmit, selectedMachine, discount, markup, targetPrice, dateFrom, dateTo, tags, pricingMode, isPriceIncrease]);

  React.useImperativeHandle(ref, () => ({
    submit: handleSubmit,
  }));

  const durationDisplay = React.useMemo(() => {
    const rawWeeks = calculateDurationWeeks(dateFrom, dateTo);
    return formatWeeksDisplay(rawWeeks);
  }, [dateFrom, dateTo]);

  const handleDateFromChange = React.useCallback((value) => {
    setDateFrom(value);
    setDateTo(value ? computeDefaultEndDate(value) : null);
  }, []);

  return (
    <Stack spacing={2.5}>
      <Typography variant="body2" color="text.secondary">
        {t('bulkMachineSubtitleShort')}
      </Typography>

      <Autocomplete
        value={selectedMachine}
        onChange={(event, newValue) => setSelectedMachine(newValue)}
        options={machines}
        getOptionLabel={(option) => option?.alias || ''}
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        renderInput={(params) => <TextField {...params} label={t('machineLabel')} size="small" fullWidth />} 
      />

      <ToggleButtonGroup
        value={priceDirection}
        exclusive
        onChange={(event, next) => {
          if (!next) return;
          setPriceDirection(next);
          setTargetPrice('');
        }}
        size="small"
        fullWidth
        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '12px', fontWeight: 600, py: 0.5 } }}
      >
        <ToggleButton value="down">{`↓ ${t('priceDecrease') || 'Bajada'}`}</ToggleButton>
        <ToggleButton value="up">{`↑ ${t('priceIncrease') || 'Subida'}`}</ToggleButton>
      </ToggleButtonGroup>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
        {pricingMode === 'price' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
            <TextField
              label={t('finalPrice') || 'Final price'}
              value={targetPrice}
              onChange={(e) => setTargetPrice(e.target.value)}
              size="small"
              InputProps={{ inputProps: { min: 0, step: 0.01, inputMode: 'decimal', pattern: '[0-9]*[.,]?[0-9]*' } }}
              fullWidth
            />
            {showCommaWarning && (
              <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5 }}>
                {t('decimalCommaWarning') || 'Use . instead of commas'}
              </Typography>
            )}
          </Box>
        ) : isPriceIncrease ? (
          <TextField
            label={t('markupLabel') || 'Subida (%)'}
            value={markup}
            onChange={(e) => setMarkup(e.target.value)}
            size="small"
            InputProps={{ inputProps: { min: 1, max: 100 } }}
            type="number"
            fullWidth
          />
        ) : (
          <TextField
            label={t('discountLabel')}
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            size="small"
            InputProps={{ inputProps: { min: 1, max: 100 } }}
            type="number"
            fullWidth
          />
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, minWidth: 80 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            {t('finalPrice') || 'Final price'}
          </Typography>
          <Switch
            checked={pricingMode === 'price'}
            onChange={(event) => {
              setPricingMode(event.target.checked ? 'price' : 'discount');
            }}
            size="small"
          />
        </Box>
      </Stack>

      <TagSelect
        label={t('tagsPlaceholder')}
        placeholder={t('tagsPlaceholder')}
        value={tags}
        onChange={setTags}
        availableTags={availableTags}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <DatePicker
          label={t('startLabel')}
          value={dateFrom}
          onChange={handleDateFromChange}
          slotProps={{ textField: { size: 'small', fullWidth: true } }}
        />
        <DatePicker
          label={t('endLabel')}
          value={dateTo}
          onChange={setDateTo}
          slotProps={{ textField: { size: 'small', fullWidth: true } }}
        />
      </Stack>

      {durationDisplay != null && (
        <Typography variant="caption" color="text.secondary">
          {`${t('experimentDurationLabel') || 'Duration'}: ${durationDisplay} ${
            durationDisplay === 1
              ? t('weeksLabelSingular') || 'week'
              : t('weeksLabelPlural') || 'weeks'
          }`}
        </Typography>
      )}

    </Stack>
  );
});

export default BulkMachineDiscount;
